import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import JarvisBoot from "./components/JarvisBoot";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import Chat from "./pages/Chat";
import Documents from "./pages/Documents";
import Analytics from "./pages/Analytics";
import Users from "./pages/Users";
import AriaSettings from "./pages/AriaSettings";
import OrgSettings from "./pages/OrgSettings";
import SuperAdmin from "./pages/SuperAdmin";
import AdminFeedback from "./pages/AdminFeedback";
import AuditLog from "./pages/AuditLog";
import SsoIdp from "./pages/SsoIdp";
import Home from "./pages/Home";
import Learn from "./pages/Learn";
import Notes from "./pages/Notes";
import TurboLearn from "./pages/TurboLearn";
import Interviews, { InterviewSessionPage } from "./pages/Interviews";
import Knowledge from "./pages/Knowledge";
import Calendar from "./pages/Calendar";
import Plan from "./pages/Plan";
import Warmup from "./pages/Warmup";
import AdvisorShare from "./pages/AdvisorShare";
import SearchPage from "./pages/SearchPage";
import PersonalSettings from "./pages/PersonalSettings";
import Applications from "./pages/Applications";
import CourseSetup from "./pages/CourseSetup";
import ProjectContext from "./pages/ProjectContext";
import ProjectConsent from "./pages/ProjectConsent";
import { Toaster } from "sonner";

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously during render to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <JarvisBoot />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/project-consent" element={<ProjectConsent />} />
      <Route path="/advisor/:token" element={<AdvisorShare />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/sso-idp" element={<SsoIdp />} />
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="home" element={<Home />} />
        <Route path="chat" element={<Chat />} />
        <Route path="learn" element={<Learn />} />
        <Route path="notes" element={<Notes />} />
        <Route path="turbolearn" element={<TurboLearn />} />
        <Route path="documents" element={<Documents />} />
        <Route path="interviews" element={<Interviews />} />
        <Route path="interviews/:id" element={<InterviewSessionPage />} />
        <Route path="knowledge" element={<Knowledge />} />
        <Route path="plan" element={<Plan />} />
        <Route path="warmup" element={<Warmup />} />
        <Route path="applications" element={<Applications />} />
        <Route path="setup" element={<CourseSetup />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="settings" element={<PersonalSettings />} />
        <Route path="project-context/:projectId" element={<ProjectContext />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="users" element={<ProtectedRoute roles={["org_admin"]}><Users /></ProtectedRoute>} />
        <Route path="audit" element={<ProtectedRoute roles={["org_admin"]}><AuditLog /></ProtectedRoute>} />
        <Route path="settings/aria" element={<ProtectedRoute roles={["org_admin"]}><AriaSettings /></ProtectedRoute>} />
        <Route path="settings/org" element={<ProtectedRoute roles={["org_admin"]}><OrgSettings /></ProtectedRoute>} />
      </Route>
      <Route path="/admin" element={<ProtectedRoute roles={["super_admin"]}><AppLayout /></ProtectedRoute>}>
        <Route index element={<SuperAdmin />} />
        <Route path="feedback" element={<AdminFeedback />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    // Crystallography Midnight: dark mode is canonical. Honor user override if set.
    const v = localStorage.getItem("aria_dark");
    const dark = v === null ? true : v === "1";
    document.documentElement.classList.toggle("dark", dark);
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
